import React, { SetStateAction, useEffect, useRef } from 'react'
import ButtonGlobal from '@shared/Button'
import { useTranslation } from 'react-i18next'
import {
  useFetchAsanSignMutation,
  useFetchRefusedSignMutation,
} from 'store/reducers/ibsigning/asansigning/sign/signApi'
import { useFetchVerifyAsanSignMutation } from 'store/reducers/ibsigning/asansigning/verify/verifyApi'
import { useFetchCheckDirectorMutation } from 'store/reducers/ibsigning/checkDirector'
import { ModalProvider } from '@shared/Modal'
import SignModalContent from './SignModalContent'
import { QueryStatus } from '@reduxjs/toolkit/query'
import {
  TypedMutationTrigger,
  TypedUseMutation,
} from '@reduxjs/toolkit/dist/query/react'
import { useAbility } from '@shared/AccessManager'
import { Tooltip } from 'antd'

export interface CompleteData {
  transactionId: string
  fullName: string
}

export const arrayBufferToBase64 = (jsonData: object) => {
  const jsonString = JSON.stringify(jsonData)
  const encoder = new TextEncoder()
  const encoded = encoder.encode(jsonString)

  let binary = ''
  const bytes = new Uint8Array(encoded)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

interface AsanSigningProps {
  checkSignCallback: () => boolean
  afterCloseFn?: () => void
  onComplete: (data: CompleteData) => void
  handleSigning: () => void
  status: QueryStatus
  asanKey: React.Key
  docType: 'SCO' | 'ALL' | 'LMT' | 'BCO'
  defaultOpen?: [open: boolean, setOpen: SetStateAction<any>]
  checkDirectorFnWithProps?: [
    checkDirectorFn: TypedUseMutation<any, any, any>,
    props: any
  ]
  message?: string
  beforeFromSigning?: () => Promise<{
    mutation: TypedMutationTrigger<any, any, any>
    requestData: {}
    fileNameKey: string
    successMessage?: string
  }>
}

export function AsanSigning({
  checkSignCallback,
  onComplete,
  handleSigning,
  status,
  asanKey,
  docType,
  defaultOpen = [false, {}],
  afterCloseFn,
  checkDirectorFnWithProps,
  message,
  beforeFromSigning,
}: AsanSigningProps) {
  const { t } = useTranslation()
  const ability = useAbility()

  const checkDirFn =
    checkDirectorFnWithProps?.[0] || useFetchCheckDirectorMutation

  const [checkDirector, { reset: cdReset }] = checkDirFn()

  const [fetchAsanSign, { data, isSuccess: isAsSuccess, reset: asReset }] =
    useFetchAsanSignMutation({ fixedCacheKey: `${asanKey}_fetchAsanSign` })

  const [
    fetchVerifyAsanSign,
    {
      data: verifyData,
      isError: isVsError,
      isSuccess: isVsSuccess,
      reset: vasReset,
    },
  ] = useFetchVerifyAsanSignMutation({
    fixedCacheKey: `${asanKey}_fetchVerifyAsanSign`,
  })

  const [refuceSignProcess, {}] = useFetchRefusedSignMutation()

  useEffect(() => {
    if (isVsSuccess) {
      asReset()
      cdReset()
      onComplete({
        fullName: data?.fullName ?? '',
        transactionId: verifyData.transactionId,
      })
    }
  }, [isVsSuccess])

  useEffect(() => {
    if (status === QueryStatus.rejected || isVsError) {
      refuceSignProcess({ transactionId: verifyData?.transactionId })
    }
  }, [status])

  return (
    <ModalProvider
      bodyStyle={status === QueryStatus.uninitialized ? {} : { padding: 40 }}
      title={t('common.attention')}
      centered
      maskClosable={false}
      closable={false}
      footer={null}
      afterClose={() => {
        asReset()
        vasReset()
        cdReset()
        defaultOpen[0] && defaultOpen[1](false)
        afterCloseFn && afterCloseFn()
      }}
      {...(status === QueryStatus.pending && { status: QueryStatus.pending })}
      {...(status === QueryStatus.fulfilled && {
        status,
        message: message || t('businessCards.operationSuccessNote'),
      })}
      {...(status === QueryStatus.rejected && {
        status,
        message,
      })}
      {...(!status && isVsSuccess
        ? { status: QueryStatus.fulfilled, message: 'Ugurlu' }
        : {})}
      modalContent={
        isAsSuccess && !(status === QueryStatus.fulfilled) ? (
          <SignModalContent asanKey={asanKey} />
        ) : null
      }
    >
      {({ openModal, setStatus }) => {
        const onSignProccess = async () => {
          openModal(QueryStatus.pending)
          let data

          try {
            if (beforeFromSigning) {
              const { mutation, requestData, fileNameKey } =
                await beforeFromSigning()
              const resp = await mutation(requestData).unwrap()
              const base64Encoded = arrayBufferToBase64(resp)

              data = {
                files: [
                  {
                    name: `${resp[fileNameKey]}.json`,
                    mimeType: 'application/json',
                    encodedBytes: base64Encoded,
                  },
                ],
              }
            } else {
              data = await handleSigning()
            }

            await checkDirector(
              checkDirectorFnWithProps?.[1] || { docType }
            ).unwrap()

            const asanRes = await fetchAsanSign({
              files: data.files,
              docType: docType,
            }).unwrap()
            setStatus({ status: '' })
            await fetchVerifyAsanSign({
              transactionId: asanRes.transactionId,
            }).unwrap()
          } catch (error) {
            setStatus({
              status: QueryStatus.rejected,
              message: `${
                beforeFromSigning
                  ? (await beforeFromSigning())?.successMessage
                  : ''
              } ${error?.data}`,
            })
          }
        }

        useEffect(() => {
          if (defaultOpen[0]) {
            setTimeout(() => {
              onSignProccess()
            }, 0)
          }
        }, [defaultOpen[0]])

        return (
          <Tooltip
            title={
              !(checkSignCallback() && ability.can('sign', 'AsanSigner'))
                ? t('orders.salaryCardOrder.restrictionTexts.loginWithAsan')
                : ''
            }
          >
            <span style={{ display: 'inline-block' }}>
              <ButtonGlobal
                type="primary"
                disabled={
                  !(checkSignCallback() && ability.can('sign', 'AsanSigner'))
                }
                color="#fff"
                {...(checkDirectorFnWithProps && {
                  style: { display: 'none' },
                })}
                onClick={onSignProccess}
              >
                {t('orders.salaryCardOrder.buttonsLabels.sign')}
              </ButtonGlobal>
            </span>
          </Tooltip>
        )
      }}
    </ModalProvider>
  )
}
